import qs from 'qs';
import type { LoaderFunction } from 'react-router';
import { redirect } from 'react-router';

import { addRedirectReason } from '@change-corgi/core-react/router';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';
import { createUtilityContext } from '@change-corgi/core-react/utilityContext';

import { buildRedirectErrorUrl, RedirectError } from '#src/shared/error';
import type { PrefetchContextData, PrefetchUserContextData } from '#src/shared/prefetch';
import type { AppRoute } from '#src/shared/routes';
import type { Session, SessionConfiguration } from '#src/shared/session';

import { isRestrictedAccessCheckNecessary } from '#src/app/shared/routes';
import { prefetchComponentData, prefetchComponentUserData } from '#src/app/shared/utils/prefetch';
import type { AppOptions } from '#src/app/types';

import { checkRestrictedAccess } from './checkRestrictedAccess';
import { ClientPrefetchError } from './errors';

type RouteLoaderData = {
	prefetchedUserData?: PrefetchUserContextData;
	prefetchedData?: PrefetchContextData;
	prefetchedSession?: Session;
};

export type RouteLoaderFunctionFactory = (routeConfig: AppRoute) => LoaderFunction;

export type ClientOptions = Pick<AppOptions, 'utilities' | 'l10n' | 'queryClient' | 'bot'> & {
	context: { session?: Session };
	/**
	 * initial session from the bootstrap
	 */
	session: Session;
	sessionConfiguration: SessionConfiguration;
};

// oxlint-disable-next-line max-lines-per-function
export function createClientRouteLoaderFactory({
	context,
	bot,
	sessionConfiguration,
	...options
}: ClientOptions): RouteLoaderFunctionFactory {
	const utilityContext = createUtilityContext(options.utilities);

	return (routeConfig) =>
		async ({ params, request }): Promise<RouteLoaderData> => {
			// using the up-to-date session from the context if available, otherwise using the initial session (from the bootstrap) from the options
			const session = context.session ?? options.session;

			const restrictedAccessCheckNecessary = isRestrictedAccessCheckNecessary(routeConfig);

			const url = new URL(request.url, window.location.origin);
			const query = qs.parse(url.search, { ignoreQueryPrefix: true });

			if (restrictedAccessCheckNecessary) {
				if (
					!(await checkRestrictedAccess({
						url,
						route: routeConfig,
						params,
						session,
						utilityContext,
					}))
				) {
					// never resolve as we want to prevent rendering the route
					// and let the page change/reload occur
					// oxlint-disable-next-line promise/avoid-new
					return new Promise(() => {});
				}
			}

			try {
				const prefetchContext = {
					utilityContext,
					track: utilityContext.tracker.__internal__track__,
					l10n: options.l10n,
					path: url.pathname,
					params,
					query,
					session,
					sessionConfiguration,
					queryClient: options.queryClient,
					bot,
				};
				const prefetchedSession = session;

				const prefetchedUserDataPromise = prefetchComponentUserData(routeConfig.component, prefetchContext);
				const [prefetchedUserData, prefetchedData] = await Promise.all([
					prefetchedUserDataPromise,
					prefetchComponentData(routeConfig.component, prefetchContext, prefetchedUserDataPromise),
				]);

				return { prefetchedUserData, prefetchedData, prefetchedSession };
			} catch (error) {
				if (error instanceof RedirectError) {
					// react-router expects Response to be thrown for redirects
					// oxlint-disable-next-line @typescript-eslint/only-throw-error
					throw redirect(
						addRedirectReason(
							ensureRedirectUrlIsSafe(buildRedirectErrorUrl(error, query), utilityContext),
							error.reason,
						),
						error.permanent ? 301 : 307,
					);
				}
				throw new ClientPrefetchError('Failed to prefetch page data', { cause: error });
			}
		};
}

/**
 * If the RedirectError was built without encoding the URI, it can break the react-router redirect due to error:
 * TypeError: Failed to execute 'set' on 'Headers': String contains non ISO-8859-1 code point.
 */
function ensureRedirectUrlIsSafe(url: string, utilityContext: UtilityContext): string {
	try {
		const headers = new Headers();
		headers.set('location', url);
		return url;
	} catch {
		void utilityContext.errorReporter.report({
			error: new Error('RedirectError was built with an unsafe URL => use encodeURIComponent on dynamic segments'),
			params: { url },
		});
		return encodeURI(url);
	}
}
